/** Fixed renderers for validated server sections. The server owns page state, order, membership, messages and analytical meaning. */
import type { ShopifyResultMessage, ShopifyResultsSection } from '@testfeed/shopify-contracts'
import { SectionFrame, BlockHeading } from '../study-builder/SectionFrame'
import { ServerResultCards } from './ServerResultCards'
import {
  OptionalText,
  ServerResultNotes,
  ServerResultPhrases,
  ServerResultClassifications,
  ServerResultInquiry,
} from './ServerResultsEvidence'
import { AudiencePatternsView } from './SegmentsTab'
import { YourQuestionsView } from './YourQuestionsTab'
import { SkeletonBodyText } from '../shims/SkeletonBodyTextShim'
import { ResultOutcomeView } from './ResultsSections'

export { ServerResultCards } from './ServerResultCards'

const MESSAGE_TONE = { error: 'critical', warning: 'warning', info: 'info' } as const
export function ServerResultMessages({ messages }: { messages: ShopifyResultMessage[] }) {
  if (!messages.length) return null
  return (
    <s-stack direction="block" gap="base">
      {messages.map((message, index) => (
        <s-banner key={index} heading={message.heading} tone={MESSAGE_TONE[message.severity]}>
          {message.text.split('\n').map((paragraph, paragraphIndex) => (
            <s-paragraph key={paragraphIndex}>{paragraph}</s-paragraph>
          ))}
        </s-banner>
      ))}
    </s-stack>
  )
}

export function ServerResultsSections({
  sections,
  onSelectTab,
}: {
  sections: ShopifyResultsSection[]
  onSelectTab?: (tab: string) => void
}) {
  return (
    <s-stack direction="block" gap="large-100">
      {sections.map((section) => (
        <ServerResultSection key={section.id} section={section} onSelectTab={onSelectTab} />
      ))}
    </s-stack>
  )
}

function ServerResultSection({
  section,
  onSelectTab,
}: {
  section: ShopifyResultsSection
  onSelectTab?: (tab: string) => void
}) {
  if (section.kind === 'questions')
    return (
      <YourQuestionsView
        data={section.data}
        intro={
          <SectionFrame {...section.header}>
            <ServerResultMessages messages={section.messages} />
            <ServerResultNotes notes={section.data.notes} />
          </SectionFrame>
        }
      />
    )
  if (section.kind === 'audience')
    return (
      <AudiencePatternsView
        data={section.data}
        intro={
          <SectionFrame {...section.header}>
            <ServerResultMessages messages={section.messages} />
            <ServerResultNotes notes={section.data.notes} />
          </SectionFrame>
        }
      />
    )
  if (section.kind === 'overview')
    return (
      <ResultOutcomeView header={section.header}>
        <ServerResultMessages messages={section.messages} />
        <Overview data={section.data} />
      </ResultOutcomeView>
    )
  if (section.kind === 'versions')
    return (
      <s-stack direction="block" gap="small-300">
        <BlockHeading>{section.header.title}</BlockHeading>
        <OptionalText text={section.header.description} />
        <ServerResultMessages messages={section.messages} />
        <ServerResultCards cards={section.data.cards} layout={section.data.layout} onSelectTab={onSelectTab} />
        <OptionalText text={section.data.footer} />
      </s-stack>
    )
  const body = (
    <s-stack direction="block" gap="base">
      <ServerResultMessages messages={section.messages} />
      <SectionBody section={section} onSelectTab={onSelectTab} />
    </s-stack>
  )
  if (section.disclosure)
    return (
      <s-section>
        <details className="response-phrase-disclosure">
          <summary>{section.header.title}</summary>
          <s-stack direction="block" gap="base">
            <OptionalText text={section.header.description} />
            <HeaderBadge badge={section.header.badge} />
            {body}
          </s-stack>
        </details>
      </s-section>
    )
  return <SectionFrame {...section.header}>{body}</SectionFrame>
}

function HeaderBadge({ badge }: { badge: ShopifyResultsSection['header']['badge'] }) {
  if (!badge) return null
  return <s-badge tone={badge.tone}>{badge.text}</s-badge>
}

type AnalysisSection = Exclude<ShopifyResultsSection, { kind: 'overview' | 'versions' | 'questions' | 'audience' }>
function SectionBody({ section, onSelectTab }: { section: AnalysisSection; onSelectTab?: (tab: string) => void }) {
  switch (section.kind) {
    case 'evidence_entry':
      return (
        <s-stack direction="block" gap="base">
          <ServerResultNotes notes={section.data.notes} />
          <s-unordered-list>
            {section.data.items.map((item) => (
              <s-list-item key={item.id}>{item.title}</s-list-item>
            ))}
          </s-unordered-list>
          <s-button onClick={() => onSelectTab?.(section.data.action.tabId)}>{section.data.action.label}</s-button>
        </s-stack>
      )
    case 'analysis_status':
      return <AnalysisStatus data={section.data} />
    case 'phrases':
      return <ServerResultPhrases data={section.data} />
    case 'classifications':
      return <ServerResultClassifications data={section.data} />
    case 'inquiry':
      return <ServerResultInquiry data={section.data} />
  }
}

function Overview({ data }: { data: Extract<ShopifyResultsSection, { kind: 'overview' }>['data'] }) {
  if (data.status === 'unavailable')
    return (
      <s-stack direction="block" gap="base">
        <BlockHeading>{data.headline}</BlockHeading>
        <s-paragraph>{data.explanation}</s-paragraph>
      </s-stack>
    )
  return (
    <s-stack direction="block" gap="base">
      <s-paragraph>{data.recommendation}</s-paragraph>
      <OptionalText text={data.detail} />
    </s-stack>
  )
}

/** The server chooses progress/state; this renderer supplies only existing placeholder geometry. */
function AnalysisStatus({ data }: { data: Extract<ShopifyResultsSection, { kind: 'analysis_status' }>['data'] }) {
  if (!data.loading) return <s-paragraph>{data.message}</s-paragraph>
  return (
    <s-stack direction="block" gap="base">
      <s-paragraph>{data.message}</s-paragraph>
      <SkeletonBodyText lines={2} />
      <s-box background="subdued" borderRadius="base" padding="base">
        <SkeletonBodyText lines={4} />
      </s-box>
    </s-stack>
  )
}
